// Desktop toolbar for floor-planning mode + a cursor-following draw readout.
// Phase 0: mode switch + 2D/3D view. Phase 1: Select/Wall tools, a selected-
// wall length field + Delete, and the live length/angle label while drawing.

import { type LengthUnit } from "../model/units";
import type { Opening, OpeningKind } from "../floor/walls";
import { createModeSwitch, createSegToggle } from "./mode-switch";
import { createActionBar } from "./action-bar";
import { tidySeparators } from "./dom";
import { keyForAction } from "./hotkeys";
import { type SectionRegistry } from "./section-registry";

export type FloorTool = "select" | "wall" | "measure";

// Inline icons (stroke = currentColor so they flip to dark ink on the blue
// .active button), mirroring desktop-ui.ts's icon-button pattern.
const ICON_SVG = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" ` +
  `stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;

// Floor tool icons: select = dashed marquee (matches the desktop Select tool),
// wall = a solid, lightly-filled wall bar, measure = horizontal ruler with
// notch marks along its edge.
const FLOOR_TOOL_ICONS: Record<FloorTool, string> = {
  select: ICON_SVG(
    '<rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke-dasharray="3.2 2.8"/>',
  ),
  wall: ICON_SVG(
    '<rect x="2.5" y="5" width="19" height="14" rx="1" ' +
      'fill="currentColor" fill-opacity="0.18"/>',
  ),
  measure: ICON_SVG(
    '<rect x="2.5" y="8" width="19" height="8" rx="1"/>' +
      '<path d="M6 8v4M10 8v2.5M14 8v4M18 8v2.5"/>',
  ),
};

// Recenter (crosshair) — identical to Model mode's Recenter button icon
// (desktop-ui.ts ICON_RECENTER): a centre dot with four ticks.
const ICON_RECENTER = ICON_SVG(
  '<circle cx="12" cy="12" r="3"/>' +
    '<path d="M12 2v4"/><path d="M12 18v4"/><path d="M2 12h4"/><path d="M18 12h4"/>',
);

export interface FloorUIState {
  view: "2d" | "3d";
  tool: FloorTool;
  unit: LengthUnit;
  roomHeightText: string; // current uniform wall height, formatted in the unit
  frontLayerOn: boolean; // marquee selects only the front (non-passthrough) layer
  lightingOn: boolean; // presentation-lighting preview (applies in the 3D view)
}

export interface FloorUIHandlers {
  setMode(m: "modeling" | "floorplan"): void;
  setView(v: "2d" | "3d"): void;
  setTool(t: FloorTool): void;
  setRotation(deg: number): void;
  setScale(pct: number): void;
  addOpening(kind: OpeningKind): void;
  flipDoor(): void; // F — flip the selected door (hinge jamb)
  switchDoorSides(): void; // Shift+F — switch the selected door's sides (swing)
  saveLayout(): void;
  saveLayoutAs(): void;
  newLayout(): void;
  importLayout(): void;
  setUnit(u: LengthUnit): void;
  setRoomHeight(text: string): void; // parse + apply the uniform wall height
  toggleFrontLayer(): void;
  toggleLighting(): void; // preview the walkthrough's presentation lighting (3D view)
  recenter(): void; // reframe the camera on the layout (mirrors Model mode's Recenter)
}

const CSS = `
#fp-ui {
  position: fixed; top: 0; left: 0; bottom: 0; z-index: 10;
  width: 192px; box-sizing: border-box; padding: 12px; /* bottom inset set live to dock height (collection-dock.ts) */
  display: none; flex-direction: column; align-items: stretch; gap: 12px;
  overflow-y: auto;
  font: 13px/1.2 system-ui, -apple-system, sans-serif; color: #e7e9f0;
  background: linear-gradient(90deg, rgba(26,27,38,0.96), rgba(20,21,30,0.92));
  border-right: 1px solid rgba(255,255,255,0.08); user-select: none;
}
#fp-ui.show { display: flex; }
#fp-ui .grp { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; }
#fp-ui .grp.hidden { display: none; }
#fp-ui .lbl { flex-basis: 100%; color: #8b90a3; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; margin: 0 0 1px; }
#fp-ui .hint { flex-basis: 100%; color: rgba(231,233,240,0.5); font-size: 11px; }
#fp-ui .sep { height: 1px; background: rgba(255,255,255,0.12); margin: 1px 0; }
/* flex-shrink:0: see the matching #dt-ui .docname rule — without it this
   overflow:hidden flex child collapses to a sliver when the sidebar overflows. */
#fp-ui .docname { font-size: 12px; flex-shrink: 0; font-weight: 600; color: #cdd2de; text-align: center; padding: 1px 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#fp-ui .docname.muted { font-weight: 400; font-style: italic; color: rgba(231,233,240,0.45); }
#fp-ui button {
  font: inherit; color: #d7dae6; cursor: pointer;
  background: #34384a; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px; padding: 5px 10px;
}
#fp-ui button:hover { background: #424862; }
#fp-ui button:disabled { opacity: 0.4; cursor: not-allowed; }
#fp-ui button:disabled:hover { background: #34384a; }
#fp-ui button.active { background: #6fa0ff; color: #11141c; border-color: #6fa0ff; }
#fp-ui button.on { background: #f2c14e; color: #1a1206; border-color: #f2c14e; }
/* Visibility "Show" group: a vertical list of label + toggle-switch rows
   (built in main.ts's floor-visibility section) — cleaner than a wrap of pills.
   State class is .vis-on (NOT .on) to avoid the yellow-fill button.on rule. */
#fp-ui .grp.vis-list { flex-direction: column; flex-wrap: nowrap; align-items: stretch; gap: 1px; min-width: 0; }
/* The base .lbl uses flex-basis:100% to force a full-WIDTH line break in a row
   layout — but here the group is a COLUMN, where that means full HEIGHT, which
   turns "Show" into a tall first column and wraps the rows beside it. Reset it. */
#fp-ui .grp.vis-list > .lbl { flex: 0 0 auto; }
/* Collapsible "Show" header: the whole label is a click target (caret at the
   right edge rotates), and collapsing hides the toggle rows via .collapsed. */
#fp-ui .grp.vis-list > .lbl.clickable { display: flex; align-items: center; cursor: pointer; }
#fp-ui .grp.vis-list > .lbl.clickable:hover { color: #b9bdcc; }
#fp-ui .grp.vis-list > .lbl.clickable::after {
  content: ""; margin-left: auto; margin-right: 6px; width: 0; height: 0;
  border-top: 4px solid transparent; border-bottom: 4px solid transparent;
  border-left: 5px solid currentColor; transform: rotate(90deg);
  transition: transform .14s ease;
}
#fp-ui .grp.vis-list.collapsed > .lbl.clickable::after { transform: rotate(0deg); }
#fp-ui .grp.vis-list.collapsed > .vis-row { display: none; }
/* box-sizing: border-box keeps the padding INSIDE the stretched width — without
   it the switch is pushed past the panel's right edge and clipped. padding-left
   is small so labels line up under the "SHOW" header. */
#fp-ui button.vis-row {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  box-sizing: border-box; min-width: 0; padding: 4px 2px; text-align: left;
  background: transparent; border-color: transparent; color: #b9bdcc;
  border-radius: 6px;
}
#fp-ui button.vis-row:hover { background: rgba(255,255,255,0.05); }
#fp-ui button.vis-row.vis-on { color: #eef0f6; }
#fp-ui .vis-row .vis-label {
  flex: 1; min-width: 0; line-height: 1.25; overflow-wrap: anywhere; /* long names (e.g. "Door/window dimensions") wrap rather than overflow */
}
#fp-ui .vis-row .vis-switch {
  flex: 0 0 auto; position: relative; width: 30px; height: 17px;
  border-radius: 999px; background: rgba(255,255,255,0.16);
  transition: background .14s ease;
}
#fp-ui .vis-row.vis-on .vis-switch { background: #f2c14e; }
#fp-ui .vis-row .vis-knob {
  position: absolute; top: 2px; left: 2px; width: 13px; height: 13px;
  border-radius: 50%; background: #e7e9f0;
  transition: transform .14s ease, background .14s ease;
}
#fp-ui .vis-row.vis-on .vis-knob { transform: translateX(13px); background: #1a1206; }
/* Tool picker: square icon buttons (tooltip + aria-label carry the name). */
#fp-ui button.icon-btn {
  width: 30px; height: 30px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
}
#fp-ui button.icon-btn svg { width: 19px; height: 19px; display: block; }
#fp-ui input {
  flex: 1; min-width: 0; font: inherit; color: #eceff6;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);
  border-radius: 7px; padding: 5px 8px;
}
#fp-ui .openings { flex-basis: 100%; display: flex; flex-direction: column; gap: 6px; }
#fp-ui .oblock { display: flex; flex-direction: column; gap: 5px; padding: 6px; border-radius: 8px; background: rgba(255,255,255,0.05); }
#fp-ui .ohead { display: flex; align-items: center; justify-content: space-between; }
#fp-ui .ohead .otag { color: rgba(231,233,240,0.8); font-weight: 600; font-size: 12px; }
#fp-ui .ofields { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; }
#fp-ui .ofield { display: flex; align-items: center; gap: 4px; min-width: 0; }
#fp-ui .ofield label { flex: 0 0 auto; color: rgba(231,233,240,0.5); font-size: 11px; }
#fp-ui .ofield input { flex: 1 1 0; min-width: 0; padding: 4px 4px; text-align: right; -moz-appearance: textfield; }
#fp-ui .ofield input::-webkit-outer-spin-button,
#fp-ui .ofield input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
#fp-ui button.x { flex: 0 0 auto; padding: 2px 7px; }
#fp-draw-label {
  position: fixed; z-index: 21; display: none; pointer-events: none;
  padding: 3px 7px; border-radius: 6px; font: 600 12px system-ui, sans-serif;
  color: #11141c; background: #ffe04a; box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
`;

export function installFloorUI(opts: {
  handlers: FloorUIHandlers;
  getState: () => FloorUIState;
  // Slice-contributed sidebar sections (ui/section-registry) — mounted after
  // the Settings group; their render() runs inside this toolbar's render().
  sections?: SectionRegistry;
}): {
  setVisible(v: boolean): void;
  render(): void;
  showSelection(info: { openings: Opening[] } | null): void;
  showDoorSelection(isDoor: boolean): void;
  showPlacementSelection(
    info: { rotationDeg: number; scale: number; dims: string } | null,
  ): void;
  showDrawLabel(text: string, clientX: number, clientY: number): void;
  hideDrawLabel(): void;
  flashSaved(): void;
  setUnsaved(dirty: boolean): void;
  setName(name: string): void;
} {
  const { handlers, getState, sections } = opts;

  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.appendChild(style);

  const root = document.createElement("div");
  root.id = "fp-ui";
  document.body.appendChild(root);

  const group = (label?: string): HTMLElement => {
    const g = document.createElement("div");
    g.className = "grp";
    if (label) {
      const l = document.createElement("span");
      l.className = "lbl";
      l.textContent = label;
      g.appendChild(l);
    }
    root.appendChild(g);
    return g;
  };
  const button = (
    parent: HTMLElement,
    text: string,
    onClick: () => void,
    className?: string,
  ) => {
    const b = document.createElement("button");
    b.textContent = text;
    if (className) b.className = className;
    b.addEventListener("click", () => {
      onClick();
      render();
    });
    parent.appendChild(b);
    return b;
  };
  const sep = () => {
    const s = document.createElement("div");
    s.className = "sep";
    root.appendChild(s);
  };
  const labelSpan = (text: string): HTMLElement => {
    const s = document.createElement("span");
    s.className = "hint";
    s.style.flexBasis = "auto";
    s.textContent = text;
    return s;
  };
  // A sub-heading inside a group (full-width uppercase caption), matching the
  // group headings — used to label sub-sections like the model UI's Draw group.
  const subLabel = (text: string): HTMLElement => {
    const s = document.createElement("span");
    s.className = "lbl";
    s.textContent = text;
    return s;
  };
  const numberInput = (): HTMLInputElement => {
    const i = document.createElement("input");
    i.type = "number";
    i.style.flex = "0 0 56px";
    return i;
  };

  root.appendChild(createModeSwitch("floorplan", (m) => handlers.setMode(m)));
  // Save / Save As / New / Import the current LAYOUT.
  const actbar = createActionBar({
    noun: "layout",
    onSave: () => handlers.saveLayout(),
    onSaveAs: () => handlers.saveLayoutAs(),
    onNew: () => handlers.newLayout(),
    onImport: () => handlers.importLayout(),
  });
  const nameLabel = document.createElement("div");
  nameLabel.className = "docname muted";
  nameLabel.textContent = "Untitled layout";
  root.appendChild(nameLabel);
  root.appendChild(actbar.el);
  sep();

  // ── View (above the tools) ───────────────────────────────────────────
  const viewGrp = group("View");
  const viewToggle = createSegToggle(
    [
      { label: "2D", value: "2d" },
      { label: "3D", value: "3d" },
    ],
    getState().view,
    (v) => handlers.setView(v as "2d" | "3d"),
  );
  viewToggle.el.style.flexBasis = "100%"; // full-width segmented control
  viewGrp.appendChild(viewToggle.el);
  // Presentation-lighting preview: see the layout under the VR walkthrough's
  // lighting without a headset. Only meaningful in 3D, so disabled in 2D.
  const btnLighting = button(viewGrp, "Lighting", () =>
    handlers.toggleLighting(),
  );
  // Recenter: reframe the camera on the room if it's been panned/orbited out of
  // view. Same crosshair icon as Model mode's Recenter.
  const btnRecenter = button(viewGrp, "", () => handlers.recenter(), "icon-btn");
  btnRecenter.innerHTML = ICON_RECENTER;
  btnRecenter.setAttribute("aria-label", "Recenter");
  btnRecenter.title = "Recenter the view on the room";
  sep();

  const toolGrp = group("Tools");
  const btnSelect = button(toolGrp, "", () => handlers.setTool("select"), "icon-btn");
  btnSelect.innerHTML = FLOOR_TOOL_ICONS.select;
  btnSelect.setAttribute("aria-label", "Select");
  const btnWall = button(toolGrp, "", () => handlers.setTool("wall"), "icon-btn");
  btnWall.innerHTML = FLOOR_TOOL_ICONS.wall;
  btnWall.setAttribute("aria-label", "Wall");
  const btnMeasure = button(toolGrp, "", () => handlers.setTool("measure"), "icon-btn");
  btnMeasure.innerHTML = FLOOR_TOOL_ICONS.measure;
  btnMeasure.setAttribute("aria-label", "Measure");
  sep();

  // Select options (shown only with the Select tool): front-layer-only marquee.
  // Currently hidden — "Front layer only" is the sole sub-option and not needed
  // on-screen (Alt during drag still works). Restore this block to bring it back.
  // const selOptGrp = group("Select");
  // const frontLayerBtn = button(selOptGrp, "Front layer only", () =>
  //   handlers.toggleFrontLayer(),
  // );
  // const flHint = document.createElement("span");
  // flHint.className = "hint";
  // flHint.textContent = "Box-select only front objects (or hold Alt).";
  // selOptGrp.appendChild(flHint);
  // sep();

  // Undo/redo are keyboard-only (Cmd/Ctrl+Z / Shift+Z / Y) and, in VR, the
  // controller face buttons — no on-screen buttons.

  // ── Contextual options (Measure readout / selected wall / selected model),
  // each shown only when its tool or selection is active. ──────────────────
  // Measure section (shown only with the Measure tool).
  // (The Measure group is a slice-contributed section now — floor/tools/measure.)

  // Selected-wall editor (hidden unless a wall is selected). Just the add-opening
  // buttons now — each door/window is positioned, sized, and deleted on-canvas
  // (drag the handles / edit the dimension pills / Backspace to remove).
  const selGrp = group("Selected wall");
  selGrp.classList.add("hidden");
  const openLbl = labelSpan("Openings");
  openLbl.style.flexBasis = "100%"; // heading on its own row → buttons sit below
  selGrp.appendChild(openLbl);
  button(selGrp, "+ Door", () => handlers.addOpening("door"));
  button(selGrp, "+ Window", () => handlers.addOpening("window"));

  // Selected-door editor (hidden unless a door opening is selected). Mirrors the
  // F / Shift+F hotkeys so the orientation toggles are discoverable on desktop.
  const doorGrp = group("Selected door");
  doorGrp.classList.add("hidden");
  const flipBtn = button(doorGrp, "Flip", () => handlers.flipDoor());
  flipBtn.title = "Flip the door (F)";
  const switchBtn = button(doorGrp, "Switch sides", () => handlers.switchDoorSides());
  switchBtn.title = "Switch the door's sides (Shift+F)";

  // Selected-model editor (hidden unless a placement is selected).
  const modelGrp = group("Selected model");
  modelGrp.classList.add("hidden");
  modelGrp.appendChild(labelSpan("Rotate °"));
  const rotInput = numberInput();
  modelGrp.appendChild(rotInput);
  modelGrp.appendChild(labelSpan("Scale %"));
  const scaleInput = numberInput();
  modelGrp.appendChild(scaleInput);
  modelGrp.appendChild(labelSpan("Size"));
  const dimsReadout = document.createElement("span");
  dimsReadout.className = "hint";
  dimsReadout.style.flexBasis = "100%";
  modelGrp.appendChild(dimsReadout);
  // No Delete button — Backspace/Delete removes the selected model.
  const pushRot = () => handlers.setRotation(Number(rotInput.value) || 0);
  const pushScale = () => handlers.setScale(Number(scaleInput.value) || 100);
  rotInput.addEventListener("change", pushRot);
  rotInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") pushRot();
  });
  scaleInput.addEventListener("change", pushScale);
  scaleInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") pushScale();
  });
  sep();

  // Slice-contributed sections (the visibility "Show" group, registered from
  // main against its visibility registry) mount here, directly under the
  // contextual editors and above the set-and-forget layout settings.
  sections?.mountAll({ group, button, subLabel });
  if (sections) sep();

  // Layout-wide settings: measurement unit and uniform room height. These are
  // set-and-forget, so they sit at the foot of the panel beneath the Show
  // toggles (mirrors Model mode, where the Reference-image controls trail).
  const setGrp = group();
  setGrp.appendChild(subLabel("Units"));
  const unitToggle = createSegToggle(
    [
      { label: "ft / in", value: "in" },
      { label: "meters", value: "m" },
    ],
    getState().unit === "m" ? "m" : "in",
    (v) => handlers.setUnit(v as LengthUnit),
  );
  unitToggle.el.style.flexBasis = "100%"; // full-width segmented control
  setGrp.appendChild(unitToggle.el);
  setGrp.appendChild(subLabel("Room height"));
  const heightRow = document.createElement("div");
  heightRow.style.cssText = "display:flex; gap:5px; flex-basis:100%; width:100%; align-items:center;";
  const heightInput = document.createElement("input");
  heightInput.type = "text";
  heightInput.placeholder = "height";
  heightRow.appendChild(heightInput);
  button(heightRow, "Set", () => handlers.setRoomHeight(heightInput.value));
  setGrp.appendChild(heightRow);
  heightInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") handlers.setRoomHeight(heightInput.value);
  });
  heightInput.addEventListener("blur", () => handlers.setRoomHeight(heightInput.value));
  sep();

  // The first-person VR walkthrough is launched from the top-left corner-controls
  // cluster (index.html #walkthrough-button), beside the VR edit + settings
  // buttons — no longer a sidebar entry here.

  // Saving / loading layouts lives in the bottom dock's Layouts collection now.

  const drawLabel = document.createElement("div");
  drawLabel.id = "fp-draw-label";
  document.body.appendChild(drawLabel);

  function render(): void {
    const s = getState();
    viewToggle.setActive(s.view);
    const vk = keyForAction("view-toggle");
    viewToggle.el.title = vk ? `Toggle 2D / 3D (${vk.toUpperCase()})` : "Toggle 2D / 3D";
    btnLighting.classList.toggle("active", s.lightingOn);
    btnLighting.disabled = s.view === "2d";
    btnLighting.title = "Preview the VR walkthrough's lighting (3D view)";
    btnSelect.classList.toggle("active", s.tool === "select");
    btnWall.classList.toggle("active", s.tool === "wall");
    btnMeasure.classList.toggle("active", s.tool === "measure");
    // Tool-button tooltips show their (rebindable) hotkey.
    const hint = (label: string, action: string) => {
      const k = keyForAction(action);
      return k ? `${label} (${k.toUpperCase()})` : label;
    };
    btnSelect.title = hint("Select", "tool-select");
    btnWall.title = hint("Wall", "layout-wall");
    btnMeasure.title = hint("Measure", "layout-measure");
    sections?.renderAll(); // slice-contributed sections sync their own state
    // Select-options group is currently hidden (see above); restore alongside it.
    // frontLayerBtn.classList.toggle("active", s.frontLayerOn);
    // selOptGrp.classList.toggle("hidden", s.tool !== "select");
    // Metres → the "m" segment; every imperial unit (in/ft) → the ft/in segment.
    unitToggle.setActive(s.unit === "m" ? "m" : "in");
    if (document.activeElement !== heightInput) heightInput.value = s.roomHeightText;
    tidySeparators(root); // collapse dividers left adjacent by hidden sections
  }
  render();

  return {
    setVisible(v: boolean): void {
      root.classList.toggle("show", v);
      if (!v) drawLabel.style.display = "none";
    },
    render,
    showSelection(info: { openings: Opening[] } | null): void {
      // Just toggles the add-opening buttons; openings are edited on-canvas.
      selGrp.classList.toggle("hidden", info === null);
      tidySeparators(root);
    },
    showDoorSelection(isDoor: boolean): void {
      // The Flip / Switch-sides buttons show only while a door opening is selected.
      doorGrp.classList.toggle("hidden", !isDoor);
      tidySeparators(root);
    },
    showPlacementSelection(
      info: { rotationDeg: number; scale: number; dims: string } | null,
    ): void {
      modelGrp.classList.toggle("hidden", info === null);
      if (info) {
        if (document.activeElement !== rotInput) {
          rotInput.value = String(Math.round(info.rotationDeg));
        }
        if (document.activeElement !== scaleInput) {
          scaleInput.value = String(Math.round(info.scale * 100));
        }
        dimsReadout.textContent = info.dims;
      }
      tidySeparators(root);
    },
    showDrawLabel(text: string, clientX: number, clientY: number): void {
      drawLabel.textContent = text;
      drawLabel.style.left = `${clientX + 14}px`;
      drawLabel.style.top = `${clientY + 14}px`;
      drawLabel.style.display = "block";
    },
    hideDrawLabel(): void {
      drawLabel.style.display = "none";
    },
    flashSaved: () => actbar.flashSaved(),
    setUnsaved: (dirty: boolean) => actbar.setUnsaved(dirty),
    setName(name: string): void {
      nameLabel.textContent = name || "Untitled layout";
      nameLabel.classList.toggle("muted", !name);
    },
  };
}
